import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { uploadFile } from "@/lib/chatUpload";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { FileSignature, FileDown, Upload, CheckCircle2, Mail } from "lucide-react";

const download = async (url, name) => {
  const res = await api.get(url, { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(res.data);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
};

export function ContractPanel() {
  const [c, setC] = useState(null);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/contract/me").then((r) => setC(r.data)).catch(() => setC({}));
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("يرجى رفع العقد بصيغة PDF فقط"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("حجم الملف أكبر من 10 ميجابايت"); return; }
    setProgress(0);
    try {
      await uploadFile("/contract/upload/init", file, setProgress);
      toast.success("تم رفع العقد الموقع، بانتظار مراجعة الإدارة");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setProgress(null);
  };

  if (!c) return null;
  if (!c.status) {
    return (
      <div className="flex items-start gap-2 text-sm text-muted-foreground mb-6 bg-muted/50 rounded-xl p-3 text-start" data-testid="contract-waiting">
        <Mail className="w-4 h-4 text-primary mt-0.5 shrink-0" /> سيصلك عقد الانضمام على بريدك الإلكتروني من الإدارة، وبعد توقيعه ترفعه من هنا.
      </div>
    );
  }
  const uploaded = c.status === "uploaded";
  const rejected = c.status === "rejected";
  return (
    <div className="rounded-xl border border-border p-4 mb-6 text-start space-y-3" data-testid="contract-panel">
      <div className="flex items-center gap-2 font-semibold"><FileSignature className="w-5 h-5 text-primary" /> عقد الانضمام</div>
      {rejected && (
        <div className="text-sm rounded-lg border border-destructive/30 bg-destructive/5 p-3" data-testid="contract-rejected">
          <div className="font-semibold text-destructive mb-1">لم يُقبل العقد — نرجو إعادة التوقيع والرفع</div>
          <div className="whitespace-pre-line" data-testid="contract-rejection-note">{c.rejection_note}</div>
        </div>
      )}
      {uploaded ? (
        <p className="text-sm text-emerald-700 flex items-center gap-1.5" data-testid="contract-uploaded"><CheckCircle2 className="w-4 h-4" /> تم استلام العقد الموقع، والإدارة تراجعه الآن.</p>
      ) : !rejected && (
        <p className="text-sm text-muted-foreground" data-testid="contract-sent">أرسلنا لك العقد على بريدك. اطبعه ووقّعه ثم ارفعه هنا بصيغة PDF.</p>
      )}
      {progress !== null && <Progress value={progress} data-testid="contract-upload-progress" />}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={() => download("/contract/me/pdf", "isnadcar-contract.pdf")} data-testid="contract-download-btn">
          <FileDown className="w-4 h-4" /> تنزيل العقد
        </Button>
        <Button size="sm" className="rounded-full gap-1.5" disabled={progress !== null} onClick={() => fileRef.current?.click()} data-testid="contract-upload-btn">
          <Upload className="w-4 h-4" /> {uploaded ? "استبدال العقد الموقع" : rejected ? "رفع العقد من جديد (PDF)" : "رفع العقد الموقع (PDF)"}
        </Button>
        <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={onFile} data-testid="contract-file-input" />
      </div>
    </div>
  );
}

export const downloadSignedContract = (fileId, name) => download(`/chat-files/file/${fileId}`, `contract-${name}.pdf`);
